import Container from "@/components/Container/Container.tsx";
import { ChevronLeft } from "@/components/Icons/utility/ChevronLeft.tsx";
import { Step } from "@/components/Stepper/interfaces/Step.ts";
import { StepperProps } from "@/components/Stepper/StepperProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { Check, ChevronDown } from "lucide-react";
import { Fragment, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useShallow } from "zustand/react/shallow";

const steps: Step[] = [
  { page: AppPage.AccommodationPicking, labelKey: "step0" },
  { page: AppPage.ExtraOptions, labelKey: "step1" },
  { page: AppPage.HolderAndPaymentInfo, labelKey: "step2" },
  { page: AppPage.ReservationOverview, labelKey: "step3" },
];

export const Stepper = ({ onBack, disableNext, onTryNext }: StepperProps) => {
  const { page, setPage } = useEngineStore(
    useShallow((state) => ({
      page: state.page,
      setPage: state.setPage,
    })),
  );
  const { t } = useTranslation();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const isAdvancing = useRef(false);

  const currentIndex = steps.findIndex((s) => s.page === page);
  const currentStep = steps[currentIndex];

  const tryAdvanceTo = useCallback(
    async (
      step: Step,
      { isClickable, isNext, onAfter }: { isClickable: boolean; isNext: boolean; onAfter?: () => void },
    ) => {
      if (!isClickable || isAdvancing.current) return;
      if (isNext && onTryNext) {
        isAdvancing.current = true;
        try {
          const result = await onTryNext();
          if (!result) {
            onAfter?.();
            return;
          }
        } finally {
          isAdvancing.current = false;
        }
      }
      setPage(step.page);
      onAfter?.();
    },
    [onTryNext, setPage],
  );

  return (
    <header className={`c-stepper${mobileExpanded ? " is-expanded" : ""}`}>
      <Container>
        <div className="c-stepper__inner">
          {onBack && (
            <button className="c-stepper__back" onClick={onBack}>
              <ChevronLeft />
            </button>
          )}

          {/* Mobile resolutions */}
          <div className="c-stepper__mobile">
            <button className="c-stepper__mobile-toggle" onClick={() => setMobileExpanded((v) => !v)}>
              <div className="c-stepper__mobile-current">
                <div className="c-stepper__circle is-current">
                  <span>{currentIndex + 1}</span>
                </div>
                <span className="c-stepper__mobile-label">{currentStep ? t(currentStep.labelKey) : ""}</span>
              </div>
              <div className="c-stepper__mobile-right">
                <span className="c-stepper__mobile-count">
                  {currentIndex + 1}/{steps.length}
                </span>
                <ChevronDown
                  size={16}
                  className={`c-stepper__mobile-arrow${mobileExpanded ? " is-open" : ""}`}
                />
              </div>
            </button>
          </div>

          {/* Larger resolutions */}
          <nav className="c-stepper__steps" aria-label="Booking steps">
            {steps.map((step, i) => {
              const isCompleted = i < currentIndex;
              const isCurrent = i === currentIndex;
              const isNext = i === currentIndex + 1;
              const isNextDisabled = isNext && disableNext;
              const isClickable = (isCompleted || isNext) && !isNextDisabled;
              const stateClass = isCompleted ? "is-completed" : isCurrent ? "is-current" : "is-upcoming";

              return (
                <Fragment key={i}>
                  {i > 0 && (
                    <div
                      key={`c-${step.page}`}
                      className={`c-stepper__connector ${i <= currentIndex ? "is-reached" : ""}`}
                    />
                  )}
                  <div
                    key={step.page}
                    className={`c-stepper__step ${stateClass}${isClickable ? " is-clickable" : ""}`}
                    onClick={() => tryAdvanceTo(step, { isClickable, isNext })}
                  >
                    <div className="c-stepper__circle">
                      {isCompleted ? <Check size={14} strokeWidth={3} /> : <span>{i + 1}</span>}
                    </div>
                    <span className="c-stepper__label">{t(step.labelKey)}</span>
                  </div>
                </Fragment>
              );
            })}
          </nav>
        </div>
      </Container>

      <div className="c-stepper__dropdown">
        {steps.map((step, i) => {
          const isCompleted = i < currentIndex;
          const isCurrent = i === currentIndex;
          const isNext = i === currentIndex + 1;
          const isNextDisabled = isNext && disableNext;
          const isClickable = (isCompleted || isNext) && !isNextDisabled;
          const stateClass = isCompleted ? "is-completed" : isCurrent ? "is-current" : "is-upcoming";

          return (
            <div
              key={step.page}
              className={`c-stepper__dropdown-item ${stateClass}${isClickable ? " is-clickable" : ""}`}
              onClick={() =>
                tryAdvanceTo(step, { isClickable, isNext, onAfter: () => setMobileExpanded(false) })
              }
            >
              <div className="c-stepper__circle">
                {isCompleted ? <Check size={13} strokeWidth={3} /> : <span>{i + 1}</span>}
              </div>
              <span className="c-stepper__dropdown-label">{t(step.labelKey)}</span>
            </div>
          );
        })}
      </div>
    </header>
  );
};
